/**
 * A small Markdown reader for the product dossiers.
 *
 * It turns the text into a tree of blocks and inline spans; the renderer then
 * builds real elements from that tree. Nothing here ever produces HTML, so a
 * dossier cannot smuggle markup or scripts into the page, and the dialect is
 * exactly what the dossiers use: a YAML front matter, headings, paragraphs,
 * bullet and numbered lists, pipe tables, block quotes, fenced code and rules,
 * with bold, italic, code and links inside.
 */

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "strong"; children: Inline[] }
  | { kind: "emphasis"; children: Inline[] }
  | { kind: "code"; text: string }
  | { kind: "link"; href: string; children: Inline[] }
  | { kind: "break" };

export type ListItem = {
  children: Inline[];
  /** A nested list, when the item indents one below itself. */
  nested?: Block;
};

export type TableRow = Inline[][];

export type Block =
  | { kind: "frontMatter"; entries: { key: string; value: string }[] }
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; children: Inline[] }
  | { kind: "paragraph"; children: Inline[] }
  | { kind: "list"; ordered: boolean; items: ListItem[] }
  | { kind: "table"; header: TableRow | undefined; rows: TableRow[] }
  | { kind: "quote"; children: Block[] }
  | { kind: "code"; language: string | undefined; text: string }
  | { kind: "rule" };

const headingPattern = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const bulletPattern = /^(\s*)[-*+]\s+(.*)$/;
const orderedPattern = /^(\s*)\d+[.)]\s+(.*)$/;
const rulePattern = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const fencePattern = /^\s*```\s*(\S*)\s*$/;
const tableRowPattern = /^\s*\|.*\|\s*$/;
const tableSeparatorPattern = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/** Reads a whole document. */
export const parseMarkdown = (source: string): Block[] => {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let at = 0;

  if (lines[0]?.trim() === "---") {
    const end = lines.findIndex((line, index) => index > 0 && line.trim() === "---");
    if (end > 0) {
      blocks.push(frontMatter(lines.slice(1, end)));
      at = end + 1;
    }
  }

  while (at < lines.length) {
    const step = readBlock(lines, at);
    if (step.block !== undefined) blocks.push(step.block);
    at = step.next;
  }
  return blocks;
};

type Step = { block: Block | undefined; next: number };

/** Reads one block starting at `at` and says where the next one begins. */
const readBlock = (lines: string[], at: number): Step => {
  const line = lines[at] ?? "";
  if (line.trim() === "") return { block: undefined, next: at + 1 };

  const fence = fencePattern.exec(line);
  if (fence !== null) return readFence(lines, at, fence[1]);

  if (rulePattern.test(line) && !bulletPattern.test(line)) {
    return { block: { kind: "rule" }, next: at + 1 };
  }

  const heading = headingPattern.exec(line);
  if (heading !== null) {
    return {
      block: {
        kind: "heading",
        level: headingLevel(heading[1] ?? "#"),
        children: parseInline(heading[2] ?? ""),
      },
      next: at + 1,
    };
  }

  if (tableRowPattern.test(line)) return readTable(lines, at);
  if (line.trimStart().startsWith(">")) return readQuote(lines, at);
  if (bulletPattern.test(line) || orderedPattern.test(line)) return readList(lines, at, 0);

  return readParagraph(lines, at);
};

type HeadingLevel = Extract<Block, { kind: "heading" }>["level"];

const headingLevel = (hashes: string): HeadingLevel => {
  const level = Math.min(Math.max(hashes.length, 1), 6);
  return level as HeadingLevel;
};

const frontMatter = (lines: string[]): Block => ({
  kind: "frontMatter",
  entries: lines.flatMap((line) => {
    const colon = line.indexOf(":");
    if (colon <= 0) return [];
    const key = line.slice(0, colon).trim();
    const value = line
      .slice(colon + 1)
      .trim()
      .replace(/^"(.*)"$/, "$1")
      .replace(/^'(.*)'$/, "$1");
    return [{ key, value }];
  }),
});

const readFence = (lines: string[], at: number, language: string | undefined): Step => {
  const body: string[] = [];
  let next = at + 1;
  while (next < lines.length && !fencePattern.test(lines[next] ?? "")) {
    body.push(lines[next] ?? "");
    next += 1;
  }
  return {
    block: { kind: "code", language: language === "" ? undefined : language, text: body.join("\n") },
    next: Math.min(next + 1, lines.length),
  };
};

/**
 * Consecutive non-blank lines that start no other block form one paragraph;
 * a soft line break inside it is a space, two trailing spaces a hard break.
 */
const readParagraph = (lines: string[], at: number): Step => {
  const parts: string[] = [];
  let next = at;
  while (next < lines.length) {
    const line = lines[next] ?? "";
    if (line.trim() === "") break;
    if (parts.length > 0 && startsBlock(line)) break;
    parts.push(line);
    next += 1;
  }
  const text = parts
    .map((part, index) =>
      index < parts.length - 1 && /\s{2,}$/.test(part) ? `${part.trimEnd()}\n` : part.trim(),
    )
    .join(" ")
    .replace(/\n /g, "\n");
  return { block: { kind: "paragraph", children: parseInline(text) }, next };
};

const startsBlock = (line: string): boolean =>
  headingPattern.test(line) ||
  fencePattern.test(line) ||
  rulePattern.test(line) ||
  tableRowPattern.test(line) ||
  line.trimStart().startsWith(">") ||
  bulletPattern.test(line) ||
  orderedPattern.test(line);

const readQuote = (lines: string[], at: number): Step => {
  const inner: string[] = [];
  let next = at;
  while (next < lines.length) {
    const line = lines[next] ?? "";
    if (!line.trimStart().startsWith(">")) break;
    inner.push(line.trimStart().replace(/^>\s?/, ""));
    next += 1;
  }
  return { block: { kind: "quote", children: parseMarkdown(inner.join("\n")) }, next };
};

/**
 * A list runs while its lines keep the same marker family at the same indent;
 * a deeper indent below an item becomes that item's nested list.
 */
const readList = (lines: string[], at: number, indent: number): Step => {
  const first = lines[at] ?? "";
  const ordered = orderedPattern.test(first) && !bulletPattern.test(first);
  const pattern = ordered ? orderedPattern : bulletPattern;
  const items: ListItem[] = [];
  let next = at;

  while (next < lines.length) {
    const line = lines[next] ?? "";
    const match = pattern.exec(line);
    if (match === null || (match[1] ?? "").length !== indent) break;
    const item: ListItem = { children: parseInline(match[2] ?? "") };
    next += 1;

    const continuation: string[] = [];
    while (next < lines.length) {
      const following = lines[next] ?? "";
      const deeper = leadingSpaces(following) > indent;
      const nestedMarker = bulletPattern.exec(following) ?? orderedPattern.exec(following);
      if (nestedMarker !== null && deeper) break;
      if (following.trim() === "" || !deeper || startsBlock(following)) break;
      continuation.push(following.trim());
      next += 1;
    }
    if (continuation.length > 0) {
      item.children = parseInline([match[2] ?? "", ...continuation].join(" "));
    }

    const below = lines[next] ?? "";
    const nestedMarker = bulletPattern.exec(below) ?? orderedPattern.exec(below);
    if (nestedMarker !== null && (nestedMarker[1] ?? "").length > indent) {
      const nested = readList(lines, next, (nestedMarker[1] ?? "").length);
      item.nested = nested.block;
      next = nested.next;
    }
    items.push(item);

    /* A blank line between two items of the same list is spacing, not the end. */
    const afterBlank = lines[next + 1] ?? "";
    const resumes = pattern.exec(afterBlank);
    if ((lines[next] ?? "").trim() === "" && resumes !== null && (resumes[1] ?? "").length === indent) {
      next += 1;
    }
  }
  return { block: { kind: "list", ordered, items }, next };
};

const leadingSpaces = (line: string): number => line.length - line.trimStart().length;

const readTable = (lines: string[], at: number): Step => {
  const rows: string[] = [];
  let next = at;
  while (next < lines.length && tableRowPattern.test(lines[next] ?? "")) {
    rows.push(lines[next] ?? "");
    next += 1;
  }
  const hasHeader = rows.length > 1 && tableSeparatorPattern.test(rows[1] ?? "");
  const cells = (row: string): TableRow =>
    row
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => parseInline(cell.trim()));
  const header = hasHeader ? cells(rows[0] ?? "") : undefined;
  const body = (hasHeader ? rows.slice(2) : rows).map(cells);
  /* A header of empty cells is how the dossiers draw a key/value table without a caption. */
  const blankHeader = header?.every((cell) => cell.length === 0) ?? false;
  return {
    block: { kind: "table", header: blankHeader ? undefined : header, rows: body },
    next,
  };
};

/**
 * Inline spans. Works left to right on one string, so a marker never reaches
 * across a block boundary; unmatched markers stay as plain text.
 */
export const parseInline = (text: string): Inline[] => {
  const out: Inline[] = [];
  let buffer = "";
  let at = 0;

  const flush = () => {
    if (buffer === "") return;
    out.push({ kind: "text", text: buffer });
    buffer = "";
  };

  while (at < text.length) {
    const rest = text.slice(at);

    if (rest.startsWith("\n")) {
      flush();
      out.push({ kind: "break" });
      at += 1;
      continue;
    }

    if (rest.startsWith("\\") && rest.length > 1) {
      buffer += rest[1];
      at += 2;
      continue;
    }

    if (rest.startsWith("`")) {
      const close = rest.indexOf("`", 1);
      if (close > 0) {
        flush();
        out.push({ kind: "code", text: rest.slice(1, close) });
        at += close + 1;
        continue;
      }
    }

    const link = /^\[([^\]]+)\]\(([^)\s]+)\)/.exec(rest);
    if (link !== null) {
      flush();
      out.push({ kind: "link", href: link[2] ?? "", children: parseInline(link[1] ?? "") });
      at += link[0].length;
      continue;
    }

    const strong = wrapped(rest, "**") ?? wrapped(rest, "__");
    if (strong !== undefined) {
      flush();
      out.push({ kind: "strong", children: parseInline(strong.inner) });
      at += strong.length;
      continue;
    }

    const emphasis = wrapped(rest, "*") ?? wrapped(rest, "_");
    if (emphasis !== undefined) {
      flush();
      out.push({ kind: "emphasis", children: parseInline(emphasis.inner) });
      at += emphasis.length;
      continue;
    }

    buffer += rest[0];
    at += 1;
  }
  flush();
  return out;
};

/** The text between a pair of markers at the start of `rest`, if the pair closes. */
const wrapped = (rest: string, marker: string): { inner: string; length: number } | undefined => {
  if (!rest.startsWith(marker)) return undefined;
  const after = rest.slice(marker.length);
  if (after === "" || /\s/.test(after[0] ?? "")) return undefined;
  /* A single underscore inside a word is a word character, not a marker. */
  const close = after.indexOf(marker);
  if (close <= 0) return undefined;
  if (/\s/.test(after[close - 1] ?? "")) return undefined;
  return { inner: after.slice(0, close), length: marker.length * 2 + close };
};

/** The plain words of a run of spans, for previews and headings' anchors. */
export const plainText = (spans: readonly Inline[]): string =>
  spans
    .map((span) => {
      if (span.kind === "text" || span.kind === "code") return span.text;
      if (span.kind === "break") return " ";
      return plainText(span.children);
    })
    .join("");
